/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState, useRef } from 'react';
import 'react-datetime/css/react-datetime.css';
import {
  Alert, Modal, Spinner,
} from 'react-bootstrap';
import _, { defaults } from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isSystemManagedStatus, systemManagedStatusLabel } from '../../../Utils/bookingStatus';
import { isExpenseBooking } from '../../../Utils/bookingType';
import { get } from '../../../Services/API';
import Error from '../../../Components/Error';
import ValidationErrorList from '../../../Components/ValidationErrorList';

import BookingService from '../../../Services/BookingService';
import OrderInputForm from './OrderInputForm';
import OrderInputPreview from './OrderInputPreview';
import LinkedBookingsSyncStep from './LinkedBookingsSyncStep';
import { activePostSaveSteps } from './postSaveSteps';
import AIExtractPanel from './AIExtractPanel';
import MapRoutePanel from '../../../Components/MapRoutePanel';

dayjs.extend(utc);

function OrderInputModal({
  originalOrder: initialOrder = {}, show = false, handleClose, fetchOrders,
}) {
  const [originalOrder, setOriginalOrderState] = useState(initialOrder);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  const defaultOrder = {
    booking_type: 'transfer',
    billing: {},
    destination_from: {},
    destination_to: {},
    date_datetime: null,
    return_date_datetime: null,
    partner_booking_reference: '',
    admin_comments: null,
    operator_cancellation_price: 0,
    partner_cancellation_price: 0,
    programme: {
      enabled: false,
      total_hours: 0,
    },
    passengers: {
      amount: 0,
      adults: 0,
      children: 0,
      infant_carrier: 0,
      booster_seat: 0,
      convertible_seat: 0,
    },
    send_notification_to_customer: false,
    discount: {
      percentage: 0,
    },
    currency: '',
    payment: {
      paid: 0,
    },
    status: 'Confirmed',
    category: {
      name: '',
      capacity: 0,
      max_luggage: 0,
    },
    // Event booking fields
    event_name: '',
    event_group: '',
    is_event_booking: false,
    passenger_manifest: [],
    // Linked bookings
    linked_booking_ids: [],
  };

  if (originalOrder?.payment?.barion_payment_id) {
    // eslint-disable-next-line no-param-reassign
    originalOrder.payment.paid = true;
  }

  const [order, setOrder] = useState(defaults(originalOrder, defaultOrder));
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [error, setError] = useState('');
  // Pre-save flow: 'form' | 'sync' | 'preview'. Post-save steps are driven by
  // the declarative registry (postSaveSteps.jsx) via `postSave` below.
  const [currentStep, setCurrentStep] = useState('form');
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  // Active post-save pipeline: { ctx, steps, index } or null. The steps that
  // apply to the save run in order, then the modal closes.
  const [postSave, setPostSave] = useState(null);
  // Linked bookings sync state
  const [syncOptions, setSyncOptions] = useState({
    // Contact & Status
    full_name: false,
    email_address: false,
    phone_number: false,
    status: false,
    // Reference & Comments
    partner_booking_reference: false,
    comments: false,
    admin_comments: false,
    // Locations
    destination_from: false,
    destination_to: false,
    // Programme
    programme: false,
    // Passengers
    passengers: false,
    // Billing
    billing: false,
  });
  const [linkedBookings, setLinkedBookings] = useState([]);
  const [loadingLinkedBookings, setLoadingLinkedBookings] = useState(false);
  // Sync progress state
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0, syncing: false });
  const [paymentTypes] = useState([
    'Cash',
    'Bank Transfer',
    'Card Transfer',
    'Mixed Payment',
    'Transfer from Partner',
  ]);
  const [orderStatuses] = useState([
    'Completed',
    'Confirmed',
    'Cancelled',
    'Request',
    'Driver No-Show',
    'Passenger No-Show',
  ]);

  // Check if this is a new order (not editing existing)
  const isNewOrder = !originalOrder?._id;

  // Lock the form when EDITING an existing Mongo Booking that's stuck in a
  // partner-ingestion-pipeline state. The lock must NOT fire for:
  //   - Add Booking (originalOrder is empty / no _id)
  //   - Partner Sync approve flow (originalOrder = DDB Data with status
  //     'Pending Ingestion' but no _id — the modal submit IS the approval)
  // Both share `isNewOrder === true`. Only block when an existing Mongo
  // row is stuck.
  const isLocked = !isNewOrder && isSystemManagedStatus(originalOrder?.status);

  const handleSubmit = async (sendNotificationToCustomer) => {
    if (isLocked) {
      // Defensive guard. The Save button should already be disabled, but if
      // it isn't (e.g., status flipped to system-managed mid-edit), refuse
      // to write — the booking will be reconciled by the backend.
      setError('This booking is owned by the partner ingestion pipeline and cannot be edited until it transitions to Confirmed.');
      return;
    }
    setLoading(true);
    // Last attempt's messages, cleared before this one. Left standing they
    // outlive the fields they describe: fix the partner, save again, and the
    // old "partner_id is a required field" is still sitting under a field
    // that is now filled in.
    setValidationErrors([]);

    order.send_notification_to_customer = sendNotificationToCustomer;

    // Capture the pre-save status so we can detect a transition INTO Cancelled
    // after the save succeeds (and only then prompt to storno documents).
    const previousStatus = originalOrder?.status;

    try {
      // Determine which fields need to be synced to linked bookings
      const fieldsToSync = Object.entries(syncOptions)
        .filter(([, enabled]) => enabled)
        .map(([field]) => field);

      const hasLinkedBookingsToSync = fieldsToSync.length > 0
        && order.linked_booking_ids?.length > 0;

      // If we have linked bookings to sync, sync them FIRST before saving main booking
      // This ensures atomicity - if any sync fails, we don't save anything
      if (hasLinkedBookingsToSync) {
        const syncData = {};
        fieldsToSync.forEach((field) => {
          syncData[field] = order[field];
        });

        const bookingIds = order.linked_booking_ids;
        setSyncProgress({ current: 0, total: bookingIds.length, syncing: true });

        // Sync each linked booking - fail fast on first error
        for (let i = 0; i < bookingIds.length; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await BookingService.updateBookingFields(bookingIds[i], syncData);
          setSyncProgress((prev) => ({ ...prev, current: i + 1 }));
        }

        setSyncProgress({ current: 0, total: 0, syncing: false });
      }

      // All linked bookings synced successfully (or none to sync), now save the main booking
      const saved = await BookingService.postBooking(order);

      fetchOrders();

      // Run the declarative post-save steps that apply to this save (visitor
      // tracking, optional Díjbekérő, storno-on-cancel, …). Conditions and
      // ordering live in postSaveSteps.jsx — add a hook there, not here.
      const ctx = {
        saved,
        order,
        partner: partners.find((p) => p.Id === order.partner_id) || order.partner,
        isNew: isNewOrder,
        justCancelled: order.status === 'Cancelled' && previousStatus !== 'Cancelled',
        fetchOrders,
      };
      const steps = activePostSaveSteps(ctx);
      if (steps.length) {
        setPostSave({ ctx, steps, index: 0 });
        return;
      }

      handleClose();
    } catch (err) {
      setCurrentStep('form');
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);

      if (err.response?.data?.errors) {
        setValidationErrors(err.response?.data?.errors);
      }
    } finally {
      setLoading(false);
      setSyncProgress({ current: 0, total: 0, syncing: false });
    }
  };

  // Handler for form -> next step
  const handleFormPreview = () => {
    if (order.linked_booking_ids?.length > 0) {
      setCurrentStep('sync');
    } else {
      setCurrentStep('preview');
    }
  };

  // Handler for going back from preview
  const handlePreviewBack = () => {
    if (order.linked_booking_ids?.length > 0) {
      setCurrentStep('sync');
    } else {
      setCurrentStep('form');
    }
  };

  const fetchPartners = async () => {
    const response = await get('bookings/partners');
    setPartners(response?.data.data);
  };

  const debouncedFetchPartners = _.debounce(() => {
    fetchPartners();
  }, 500);

  useEffect(() => {
    debouncedFetchPartners();
  }, []);

  // Ref to prevent double fetching
  const fetchedRef = useRef(false);

  // Fetch fresh booking data when modal opens for editing
  useEffect(() => {
    const fetchBooking = async () => {
      if (show && initialOrder?._id && !fetchedRef.current) {
        fetchedRef.current = true;
        setIsLoadingOrder(true);
        try {
          const freshOrder = await BookingService.getBookingById(initialOrder._id);
          if (freshOrder) {
            if (freshOrder?.payment?.barion_payment_id) {
              freshOrder.payment.paid = true;
            }
            setOriginalOrderState(freshOrder);
            setOrder(defaults(freshOrder, defaultOrder));
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error fetching booking:', err);
        } finally {
          setIsLoadingOrder(false);
        }
      }
    };

    fetchBooking();
  }, [show, initialOrder?._id]);

  // Reset fetchedRef when modal closes
  useEffect(() => {
    if (!show) {
      fetchedRef.current = false;
    }
  }, [show]);

  // Handle AI extraction results - merge with existing order
  const handleApplyExtraction = (extractedBooking) => {
    setOrder((prevOrder) => ({
      ...prevOrder,
      ...extractedBooking,
      // Merge nested objects properly
      passengers: {
        ...prevOrder.passengers,
        ...extractedBooking.passengers,
      },
      destination_from: {
        ...prevOrder.destination_from,
        ...extractedBooking.destination_from,
      },
      destination_to: {
        ...prevOrder.destination_to,
        ...extractedBooking.destination_to,
      },
      category: {
        ...prevOrder.category,
        ...extractedBooking.category,
      },
      payment: {
        ...prevOrder.payment,
        ...extractedBooking.payment,
      },
      // Preserve event fields if they exist in current order (don't overwrite with extraction)
      event_name: prevOrder.event_name || extractedBooking.event_name || '',
      event_group: prevOrder.event_group || extractedBooking.event_group || '',
      is_event_booking: prevOrder.is_event_booking || extractedBooking.is_event_booking || false,
      passenger_manifest: extractedBooking.passenger_manifest || prevOrder.passenger_manifest || [],
      // Mark this booking as having originated from the AI extract
      // panel. Backend persists the flag only on insert (immutable
      // afterwards) and surfaces it via the ai_extraction_used filter
      // for stats. Once true on this form session, stays true through
      // any operator edits before save.
      ai_extraction_used: true,
    }));
  };

  // Map panel state
  const [isMapCollapsed, setIsMapCollapsed] = useState(false);

  // Ref for scrolling to top of modal body
  const modalBodyRef = useRef(null);

  // Scroll to top when entering preview or sync mode
  useEffect(() => {
    if ((currentStep === 'preview' || currentStep === 'sync') && modalBodyRef.current) {
      modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  // Fetch linked bookings when entering sync step
  useEffect(() => {
    const fetchLinkedBookings = async () => {
      if (currentStep === 'sync' && order.linked_booking_ids?.length > 0) {
        setLoadingLinkedBookings(true);
        try {
          const response = await get('/bookings', {
            filters: { _id: { $in: order.linked_booking_ids } },
          });
          setLinkedBookings(response.data?.data?.documents || []);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error fetching linked bookings:', err);
          setLinkedBookings([]);
        } finally {
          setLoadingLinkedBookings(false);
        }
      }
    };
    fetchLinkedBookings();
  }, [currentStep, order.linked_booking_ids]);

  // Check if we have valid coordinates for map
  const hasValidMapCoords = order.destination_from?.latitude
    && order.destination_from?.longitude
    && order.destination_to?.latitude
    && order.destination_to?.longitude;

  // Track previous coordinates to detect changes and reset estimates
  const prevCoordsRef = useRef({
    fromLat: null,
    fromLon: null,
    toLat: null,
    toLon: null,
  });

  // Reset route estimates when coordinates change
  React.useEffect(() => {
    const currentCoords = {
      fromLat: order.destination_from?.latitude,
      fromLon: order.destination_from?.longitude,
      toLat: order.destination_to?.latitude,
      toLon: order.destination_to?.longitude,
    };

    const prevCoords = prevCoordsRef.current;

    // Check if any coordinate has changed (and we had previous coordinates)
    const coordsChanged = (prevCoords.fromLat !== null || prevCoords.toLat !== null)
      && (
        prevCoords.fromLat !== currentCoords.fromLat
        || prevCoords.fromLon !== currentCoords.fromLon
        || prevCoords.toLat !== currentCoords.toLat
        || prevCoords.toLon !== currentCoords.toLon
      );

    if (coordsChanged) {
      // Reset the route estimates since coordinates changed
      setOrder((prevOrder) => ({
        ...prevOrder,
        estimated_distance: null,
        estimated_duration: null,
      }));
    }

    // Update the ref with current coordinates
    prevCoordsRef.current = currentCoords;
  }, [
    order.destination_from?.latitude,
    order.destination_from?.longitude,
    order.destination_to?.latitude,
    order.destination_to?.longitude,
  ]);

  // Handle route calculation results
  const handleRouteCalculated = ({ distance, duration }) => {
    setOrder((prevOrder) => ({
      ...prevOrder,
      estimated_distance: distance,
      estimated_duration: duration,
    }));
  };

  // Advance the post-save pipeline; close the modal once the steps are done.
  const advancePostSave = () => {
    if (!postSave) return;
    if (postSave.index + 1 < postSave.steps.length) {
      setPostSave({ ...postSave, index: postSave.index + 1 });
    } else {
      setPostSave(null);
      handleClose();
    }
  };

  const activeStep = postSave?.steps[postSave.index];
  const postSaveApi = { next: advancePostSave, showCloseWarning, setShowCloseWarning };
  // Show the pre-save form/preview unless an in-body post-save step is active
  // (overlay steps like storno keep the form visible underneath).
  const showForm = !postSave || !!activeStep?.overlay;

  // Intercept close while a post-save step asks to confirm (e.g. visitor tracking).
  const handleModalClose = () => {
    if (activeStep?.confirmClose) {
      if (showCloseWarning) handleClose();
      else setShowCloseWarning(true);
      return;
    }
    handleClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleModalClose} fullscreen>
        <Modal.Header closeButton>
          <Modal.Title>
            { order._id && (
            <>
              { isExpenseBooking(order) ? 'Edit expense' : 'Edit order' }
              { ' ' }
              { order.booking_reference }
            </>
            ) }
            { !order._id && (isExpenseBooking(order) ? 'New expense' : 'New order') }
          </Modal.Title>
        </Modal.Header>
        <Modal.Body ref={modalBodyRef}>
          {isLoadingOrder ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-3">Loading booking details...</p>
            </div>
          ) : (
            <div className="d-flex gap-3">
              {/* Left side - Form content */}
              <div className={`flex-grow-1 ${hasValidMapCoords && !isMapCollapsed ? '' : 'w-100'}`}>
                <Error
                  error={error}
                  setError={setError}
                />

                {/* The server's own messages. `Error` above shows only the
                    envelope's "The validation has failed", which on its own
                    is what the office was left with. */}
                <ValidationErrorList errors={validationErrors} />

                {isLocked && (
                <Alert
                  variant={originalOrder?.status === 'Ingestion Failed' ? 'danger' : 'info'}
                  className="d-flex align-items-center"
                >
                  <i className={`fa ${originalOrder?.status === 'Ingestion Failed' ? 'fa-triangle-exclamation' : 'fa-hourglass-half'} me-2`} />
                  <div>
                    <strong>{originalOrder?.status}</strong>
                    {' — '}
                    {systemManagedStatusLabel(originalOrder?.status)}
                  </div>
                </Alert>
                )}

                {/* AI Extraction Panel - only shown for new orders in form step */}
                {showForm && isNewOrder && currentStep === 'form'
                  && !isExpenseBooking(order) && (
                  <AIExtractPanel onApplyExtraction={handleApplyExtraction} />
                )}

                {/* Step 1: Form */}
                {showForm && currentStep === 'form' && (
                <OrderInputForm
                  setOriginalOrder={setOrder}
                  originalOrder={order}
                  validationErrors={validationErrors}
                  partners={partners}
                  orderStatuses={orderStatuses}
                  paymentTypes={paymentTypes}
                  onPreview={handleFormPreview}
                />
                )}

                {/* Step 2: Linked Bookings Sync (optional) */}
                {showForm && currentStep === 'sync' && (
                <LinkedBookingsSyncStep
                  order={order}
                  linkedBookings={linkedBookings}
                  syncOptions={syncOptions}
                  onSyncOptionsChange={setSyncOptions}
                  onBack={() => setCurrentStep('form')}
                  onContinue={() => setCurrentStep('preview')}
                  loading={loadingLinkedBookings}
                />
                )}

                {/* Step 3: Preview */}
                {showForm && currentStep === 'preview' && (
                <OrderInputPreview
                  order={order}
                  partners={partners}
                  loading={loading}
                  handleSubmit={handleSubmit}
                  onBack={handlePreviewBack}
                  syncOptions={syncOptions}
                  linkedBookingsCount={order.linked_booking_ids?.length || 0}
                  syncProgress={syncProgress}
                />
                )}

                {/* Post-save steps that replace the body (visitor tracking, Díjbekérő, …) */}
                {postSave && activeStep && !activeStep.overlay
                  && activeStep.render(postSave.ctx, postSaveApi)}
              </div>

              {/* Right side - Map panel (only when coordinates exist and in form step) */}
              {hasValidMapCoords && currentStep === 'form' && (
              <div
                className="map-panel-container"
                style={{
                  width: isMapCollapsed ? '50px' : '400px',
                  minWidth: isMapCollapsed ? '50px' : '400px',
                  transition: 'width 0.3s ease, min-width 0.3s ease',
                }}
              >
                <MapRoutePanel
                  fromCoords={{
                    lat: order.destination_from.latitude,
                    lon: order.destination_from.longitude,
                  }}
                  toCoords={{
                    lat: order.destination_to.latitude,
                    lon: order.destination_to.longitude,
                  }}
                  fromAddress={order.destination_from.address}
                  toAddress={order.destination_to.address}
                  stops={order.programme?.stops || []}
                  onRouteCalculated={handleRouteCalculated}
                  isCollapsed={isMapCollapsed}
                  onToggleCollapse={() => setIsMapCollapsed(!isMapCollapsed)}
                />
              </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Post-save steps rendered as their own overlay (e.g. storno-on-cancel) */}
      {postSave && activeStep?.overlay && activeStep.render(postSave.ctx, postSaveApi)}
    </>
  );
}

export default OrderInputModal;
